import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';

import MobileBottomNav from '@/components/ui/MobileBottomNav';
import { ROUTES } from '@/configs/routes';
import { useKeyboardOpen } from '@/hooks/useKeyboardOpen';
import { useLocalStore } from '@/hooks/useLocalStore';
import { useLockBodyScroll } from '@/hooks/useLockBodyScroll';
import { useOrganizationLayoutViewState } from '@/hooks/useOrganizationLayoutViewState';
import { useOrganizationSectionsAccessGuard } from '@/hooks/useOrganizationSectionsAccessGuard';
import { organizationsStore } from '@/stores/global/RootStore';
import {
  OrganizationSectionsStore,
  OrganizationSectionsStoreProvider,
} from '@/stores/local/shared/OrganizationSectionsStore';
import { SidebarDocumentsStore, SidebarDocumentsStoreProvider } from '@/stores/local/sidebar/SidebarDocumentsStore';
import { SidebarNoteStore, SidebarNoteStoreProvider } from '@/stores/local/sidebar/SidebarNoteStore';
import { useDevices } from '@/utils/devices';

import Back from './Back';
import MobileHeader from './MobileHeader';
import OrganizationLayoutContent from './OrganizationLayoutContent';
import Sidebar from './Sidebar';
import { useOrganizationBottomNav } from './useOrganizationBottomNav';

import s from './OrganizationLayout.module.scss';

const OrganizationLayout: React.FC = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const { isCompactViewport } = useDevices();
  const organizationId = Number(id);
  const normalizedOrganizationId = Number.isFinite(organizationId) ? organizationId : 0;

  const sidebarDocumentsStore = useLocalStore(
    () =>
      new SidebarDocumentsStore({
        organizationId: normalizedOrganizationId,
        getOrganizationName: () =>
          organizationsStore.getOrganizationById(String(normalizedOrganizationId))?.name ?? null,
      }),
  );
  const sidebarNoteStore = useLocalStore(() => new SidebarNoteStore({ organizationId }));
  const organizationSectionsStore = useLocalStore(() => new OrganizationSectionsStore());

  React.useEffect(() => {
    sidebarDocumentsStore.init();
  }, [sidebarDocumentsStore]);

  React.useEffect(() => {
    organizationSectionsStore.loadSections(normalizedOrganizationId);
  }, [organizationSectionsStore, normalizedOrganizationId]);

  const {
    detailKind,
    enabledTopLevelSections,
    hasEnabledTopLevelSections,
    hasSidebarSections,
    isSectionsConfigLoading,
    isSectionsConfigError,
    showNoSectionsStub,
    showMobileBottomNav,
  } = useOrganizationLayoutViewState({
    pathname,
    organizationSectionsStore,
  });

  useOrganizationSectionsAccessGuard({
    id,
    pathname,
    isPhone: isCompactViewport,
    normalizedOrganizationId,
    enabledTopLevelSections,
    hasEnabledTopLevelSections,
    isSectionsConfigLoading,
    isSectionsConfigError,
    organizationSectionsStore,
  });

  const {
    items: bottomNavItems,
    activeKey: activeBottomTab,
    onChange: handleBottomNavChange,
  } = useOrganizationBottomNav(enabledTopLevelSections, {
    getLabel: (key) => organizationSectionsStore.getSectionName(key),
  });
  const shouldShowBottomNav =
    !isSectionsConfigLoading && !isSectionsConfigError && showMobileBottomNav && hasEnabledTopLevelSections;
  const shouldShowSidebar =
    !isCompactViewport && !isSectionsConfigLoading && !isSectionsConfigError && hasSidebarSections;
  const isChatPage = Boolean(
    matchPath(ROUTES.organization.documents.path, pathname) ||
    matchPath(ROUTES.organization.documents.chat.path, pathname) ||
    matchPath(ROUTES.organization.documents.document.analyze.chat.path, pathname) ||
    matchPath(ROUTES.organization.macro.analyze.chat.path, pathname),
  );
  const isChatKeyboardOpen = useKeyboardOpen({ enabled: isCompactViewport && isChatPage });
  const isNotebookUpsertPage =
    detailKind === 'noteCreate' ||
    detailKind === 'noteEdit' ||
    detailKind === 'insightCreate' ||
    detailKind === 'insightEdit' ||
    detailKind === 'followUpCreate' ||
    detailKind === 'followUpEdit';
  const isKeyboardControlledPage = isChatPage || isNotebookUpsertPage;

  useLockBodyScroll({ lock: isCompactViewport && isKeyboardControlledPage });

  const handleRetryLoadSections = React.useCallback(() => {
    organizationSectionsStore.loadSections(normalizedOrganizationId);
  }, [organizationSectionsStore, normalizedOrganizationId]);

  return (
    <OrganizationSectionsStoreProvider store={organizationSectionsStore}>
      <SidebarDocumentsStoreProvider store={sidebarDocumentsStore}>
        <SidebarNoteStoreProvider store={sidebarNoteStore}>
          <div
            className={cn(
              s.root,
              detailKind && s.root_details,
              isChatPage && s.root_chat,
              isNotebookUpsertPage && s.root_keyboard_locked,
              !shouldShowSidebar && s.root_without_sidebar,
            )}
          >
            <div className={s.root__header}>{isCompactViewport ? <MobileHeader /> : <Back />}</div>
            {shouldShowSidebar && (
              <div className={s.root__sidebar}>
                <Sidebar />
              </div>
            )}
            <div className={s.root__content}>
              <OrganizationLayoutContent
                isSectionsConfigLoading={isSectionsConfigLoading}
                isSectionsConfigError={isSectionsConfigError}
                showNoSectionsStub={showNoSectionsStub}
                onRetryLoadSections={handleRetryLoadSections}
              />
            </div>
            {shouldShowBottomNav && !isChatKeyboardOpen && (
              <MobileBottomNav
                className={s['root__bottom-nav']}
                items={bottomNavItems}
                activeKey={activeBottomTab}
                onChange={handleBottomNavChange}
              />
            )}
          </div>
        </SidebarNoteStoreProvider>
      </SidebarDocumentsStoreProvider>
    </OrganizationSectionsStoreProvider>
  );
};

export default observer(OrganizationLayout);
